import * as React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ISearchBox, SearchBox, Stack } from '@fluentui/react'
import { useDispatch, useSelector } from 'react-redux'
import { Store } from '../../reducers'
import FileList, { FileListFilter, IFileListSettings, UserSearchMode } from '../../components/List/File/FileList'
import { FileAction, FileStatus, IOverviewItemDto } from '../../config/fileConfig'
import ActionHeader from '../../components/Render/ActionHeader'
import { DataPromise } from '../../context/AxiosContext'
import Loading from '../../components/Loading'
import { useMount } from '@fluentui/react-hooks'
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Field,
    makeStyles,
    Switch,
} from '@fluentui/react-components'
import useUser from '../../hooks/useUser'
import useRoles, { Role } from '../../hooks/useRoles'
import { IActionButton } from '../../components/ActionButtons'
import useViewport, { Viewport } from '../../hooks/useViewport'
import AttributesList, { parseAttributeToParams } from '../../components/Form/Attributes/AttributesList'
import { AppAction } from '../../config/appConfig'
import { MenuItemKey } from '../../components/Menu'
import useAxios from '../../hooks/useAxios'
import { ModuleType } from '../../context/GlobalContext'
import useModules from '../../hooks/module/useModules'
import UserPicker from '../../components/TagPicker/UserPicker'
import { useTranslation } from 'react-i18next'
import FileStatusPicker from '../../components/TagPicker/FileStatusPicker'
import { AttributeContextType, AttributeViewType, EntityAttributeType } from '../../hooks/useAttributes'
import CsvReportButton from '../../components/Button/CsvReportButton'
import { DocumentGenerationType } from '../../hooks/document/useDocumentGenerationHandler'

const useStyles = makeStyles({
    doubleField: {
        display: 'grid',
        gridTemplateColumns: '25% 1fr',
    },
})

const parseSettings = (settings: IFileListSettings): IFileListSettings => {
    if (settings === undefined) {
        return undefined
    }

    return {
        ...settings,
        query: settings.query?.length >= settings.minQueryLength ? settings.query : '',
    }
}

export const parseFileSettingsToParams = (settings: IFileListSettings): any => {
    return {
        query: settings.query,
        ...settings.filter,
        attributes: parseAttributeToParams(settings.filter.attributes),
    }
}

function resetFilterToDefault(currentUserId: string, settings?: IFileListSettings) {
    return {
        ...settings,
        query: settings?.query ?? '',
        filter: {
            ownerUsers: settings?.filter.ownerUsers ?? [currentUserId],
            userSearchMode: settings?.userSearchMode ?? UserSearchMode.OR,
            executiveUsers: settings?.filter.executiveUsers ?? [currentUserId],
            statuses: settings?.filter.statuses ?? [FileStatus.Open],
            attributes: settings?.filter.attributes ?? [],
        },
    }
}

export default function FileOverview(props) {
    const dispatch = useDispatch()
    const axios = useAxios()
    const modules = useModules()
    const roles = useRoles()
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const store = useSelector<Store, DataPromise<IOverviewItemDto[], IFileListSettings>>(state => state.files.overview)
    const [settings, setSettings] = useState<IFileListSettings>(undefined)
    const [query, setQuery] = useState<string>(store.settings?.query)
    const [preQuerySettings, setPreQuerySettings] = useState<IFileListSettings>(undefined)
    const [previousSettings, setPreviousSettings] = useState<string>(undefined)

    const { id: currentUserId } = useUser()
    const { isViewport, viewport } = useViewport()
    const orientation = useMemo(() => (isViewport('<', Viewport.XL) ? 'vertical' : 'horizontal'), [viewport])
    const searchBoxRef = useRef<ISearchBox>()

    const updateFilter = (filter?: Partial<FileListFilter>): any => {
        if (filter === undefined) {
            setSettings({
                ...settings,
                filter: {
                    ...settings.filter,
                },
            })
        } else {
            setSettings({
                ...settings,
                filter: {
                    ...settings.filter,
                    ...filter,
                    attributes: [...(settings.filter?.attributes || []), ...(filter.attributes || [])].filter(
                        (value, index, array) => array.indexOf(value) === index,
                    ),
                },
            })
        }
    }

    //Reset breadcrumbs and data
    useEffect(() => {
        dispatch({ type: AppAction.SetBreadcrumbs, payload: [MenuItemKey.Files] })
        dispatch({ type: FileAction.ResetCurrent })
    }, [props])

    //Update store settings once with (default) values
    useMount(() => {
        dispatch({ type: FileAction.OverviewSettings, payload: resetFilterToDefault(currentUserId, store.settings) })
    })

    // Set settings once store settings have been fetched
    useEffect(() => {
        if (settings === undefined && store.settings !== undefined && store.settings.filter !== undefined) {
            setSettings(store.settings)
        }
    }, [store.settings])

    //Check if settings have changed enough to invoke a GET /file
    useEffect(() => {
        if (settings !== undefined) {
            const nextSettings = parseSettings(store.settings)

            if (JSON.stringify(nextSettings) !== previousSettings) {
                dispatch({
                    type: FileAction.GetOverview,
                    payload: axios.get('/file', { params: parseFileSettingsToParams(nextSettings) }),
                })
                setPreviousSettings(JSON.stringify(nextSettings))
            }
        }
    }, [store.settings])

    // Set the focus to the SearchBox
    useEffect(() => {
        if (query?.length > 0 && searchBoxRef.current) {
            searchBoxRef.current.focus()
        }
    }, [searchBoxRef, store.settings])

    // Update settings once either a query or an owner has been selected
    useEffect(() => {
        if (settings !== undefined) {
            dispatch({
                type: FileAction.OverviewSettings,
                payload: { ...settings, query, filter: { ...settings?.filter } },
            })
        }
    }, [query, settings])

    const actionButtonItems: IActionButton[] = useMemo(
        () =>
            [
                roles.none([Role.FileLimited]) && {
                    key: 'add-file',
                    title: translate('action.create.file.title'),
                    secondaryText: translate('action.create.file.subtitle'),
                    iconName: 'Album',
                    route: '/file/add',
                },
            ].filter(Boolean),
        [],
    )

    if (settings?.filter === undefined) {
        return <Loading />
    }

    return (
        <>
            <ActionHeader actionButtonItems={actionButtonItems}>
                <Field label={translate('file.filter.owner')} orientation={orientation}>
                    <UserPicker
                        selectedOptions={settings.filter.ownerUsers || []}
                        onOptionsSelect={(_, selectedOptions) => updateFilter({ ownerUsers: selectedOptions })}
                    />
                </Field>
                <Field label={translate('file.filter.executive')} orientation={orientation}>
                    <div className={styles.doubleField}>
                        <Switch
                            checked={settings.filter.userSearchMode === UserSearchMode.OR}
                            label={
                                settings.filter.userSearchMode === UserSearchMode.OR
                                    ? translate('file.filter.searchMode.or')
                                    : translate('file.filter.searchMode.and')
                            }
                            onChange={(_, data) =>
                                updateFilter({ userSearchMode: data.checked ? UserSearchMode.OR : UserSearchMode.AND })
                            }
                        />
                        <UserPicker
                            selectedOptions={settings.filter.executiveUsers || []}
                            onOptionsSelect={(_, selectedOptions) => updateFilter({ executiveUsers: selectedOptions })}
                        />
                    </div>
                </Field>
                <Field label={translate('file.status.label')} orientation={orientation}>
                    <FileStatusPicker
                        selectedOptions={settings.filter.statuses || []}
                        onOptionsSelect={(_, selectedOptions) =>
                            updateFilter({ statuses: selectedOptions as FileStatus[] })
                        }
                    />
                </Field>
                {modules.has(ModuleType.Attributes) && (
                    <Accordion collapsible>
                        <AccordionItem value="attributes">
                            <AccordionHeader>{translate('file.filter.attributes')}</AccordionHeader>
                            <AccordionPanel>
                                <AttributesList
                                    orientation={orientation}
                                    entityType={EntityAttributeType.File}
                                    context={[AttributeContextType.File]}
                                    viewType={AttributeViewType.Filter}
                                    entity={settings.filter}
                                    setFieldValue={() => updateFilter()}
                                />
                            </AccordionPanel>
                        </AccordionItem>
                    </Accordion>
                )}
            </ActionHeader>
            <SearchBox
                className="clear"
                placeholder={translate('file.filter.search')}
                underlined
                componentRef={searchBoxRef}
                value={query ?? ''}
                onChange={(_event?: React.ChangeEvent<HTMLInputElement>, newQuery?: string) => {
                    setQuery(newQuery ?? '')
                    //number of characters reached threshold, clear users to start searching.
                    if (query?.length < settings.minQueryLength && newQuery?.length === settings.minQueryLength) {
                        setPreQuerySettings(settings)
                        setSettings({
                            ...settings,
                            filter: {
                                ownerUsers: [],
                                userSearchMode: undefined,
                                executiveUsers: [],
                                statuses: [],
                                attributes: settings?.filter.attributes ?? [],
                            },
                        })
                    }
                    //number of characters is under threshold, set users to old value.
                    else if (query?.length === settings.minQueryLength && newQuery?.length < settings.minQueryLength) {
                        if (preQuerySettings) {
                            setSettings(preQuerySettings)
                        } else {
                            setSettings(
                                resetFilterToDefault(currentUserId, {
                                    ...store.settings,
                                    query: '',
                                    filter: {},
                                }),
                            )
                        }
                    }
                }}
                onClear={() => {
                    setQuery('')
                    if (preQuerySettings) {
                        setSettings(preQuerySettings)
                    } else {
                        setSettings(
                            resetFilterToDefault(currentUserId, {
                                ...store.settings,
                                query: '',
                                filter: {},
                            }),
                        )
                    }
                }}
            />
            {roles.all([Role.Report]) && (
                <Stack horizontal>
                    <CsvReportButton<FileListFilter & { query: string }>
                        generationType={DocumentGenerationType.FileXls}
                        payload={() => parseFileSettingsToParams({ ...settings, query })}
                    />
                </Stack>
            )}
            <FileList files={store} />
        </>
    )
}
